import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from './../environments/environment';


export class BaseService<T, TList> {
  // Ensure baseUrl always ends with a trailing slash
  baseUrl = environment.apiUrl.endsWith('/') ? environment.apiUrl : `${environment.apiUrl}/`;

  constructor(protected http: HttpClient, protected controller: string) {
    // Remove leading slash from controller if it exists
    if (this.controller && this.controller.startsWith('/')) {
      this.controller = this.controller.substring(1);
    }
  }

  get<T>(action?: string): Observable<T | any> {
    const url = this.createUrl(action);
    return this.http.get<T>(url)
      .pipe(
        catchError(this.handleError<T>())
      );
  }

  getCustomType<customT>(action: string): Observable<customT> {
    const url = this.createUrl(action);
    return this.http.get<customT>(url)
      .pipe(
        catchError(this.handleError<customT>())
      );
  }

  getAll(action?: string): Observable<TList[]> {
    const url = this.createUrl(action);
    return this.http.get<TList[]>(url)
      .pipe(
        catchError(this.handleError<TList[]>([]))
      );
  }

  getAllCustomtype<customT>(action?: string): Observable<customT[]> {
    const url = this.createUrl(action);
    return this.http.get<customT[]>(url)
      .pipe(
        catchError(this.handleError<customT[]>([]))
      );
  }

  post(action: string, item: T | any) {
    const url = this.createUrl(action);
    return this.http.post<T>(url, item)
      .pipe(
        catchError(this.handleError<T>())
      );
  }

  patch(action: string, item: T | any) {
    const url = this.createUrl(action);
    return this.http.patch<T>(url, item)
      .pipe(
        catchError(this.handleError<T>())
      );
  }

  put<T>(action: string, item: T) {
    const url = this.createUrl(action);
    return this.http.put<T>(url, item)
      .pipe(
        catchError(this.handleError<T>())
      );
  }

  delete(id: number | string) {
    const url = this.createUrl(id.toString());
    return this.http.delete(url)
      .pipe(
        catchError(this.handleError<T>())
      );
  }

  /**
   * Creates a properly formatted URL by combining baseUrl, controller, and action
   * @param action Optional action/path to append (without leading slash)
   * @returns A properly formatted URL
   */
  protected createUrl(action?: string): string {
    if (!action) {
      return `${this.baseUrl}${this.controller}`;
    }
    // If action starts with '?', don't add a slash
    if (action.startsWith('?')) {
      return `${this.baseUrl}${this.controller}${action}`;
    }
    // Remove leading slash from action if it exists
    if (action.startsWith('/')) {
      action = action.substring(1);
    }
    return `${this.baseUrl}${this.controller}/${action}`;
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param result - optional value to return as the observable result
   */
  handleError<T>(result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead
      return throwError(() => error);
    };
  }
}
